/**
 * Help mode state, backed by the user-preferences store.
 *
 * Persistence is that store's: localStorage, keyed by user id. So the
 * setting is per person AND per browser, which is exactly what was
 * asked for -- enough to stop the nudge pestering someone who has
 * already read the help, and deliberately not a record that anybody was
 * trained.
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { useUserPreferences } from '../state/useUserPreferences';
import { HelpModeContext, type HelpModeValue } from './help-mode';

export function HelpProvider({ children }: { children: ReactNode }) {
  const { prefs, update } = useUserPreferences();
  const enabled = prefs.help.mode;
  const seen = prefs.help.seen;

  const setEnabled = useCallback(
    (next: boolean) => {
      // `seen` latches ON and never off. It answers "has this person
      // found the help", and finding it once is permanent -- turning
      // help off again is not un-finding it, and re-nudging someone who
      // deliberately closed it is how a nudge becomes an irritation.
      update({ help: { mode: next, seen: seen || next } });
    },
    [seen, update],
  );

  const value = useMemo<HelpModeValue>(
    () => ({ enabled, seen, setEnabled, toggle: () => setEnabled(!enabled) }),
    [enabled, seen, setEnabled],
  );

  return <HelpModeContext.Provider value={value}>{children}</HelpModeContext.Provider>;
}
